/**
 * Carton / packaging purchase math — unit cost always rounded to 2 decimals.
 */

/** Round money to 2 places after the decimal separator (0.01). */
export function roundMoney2(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/**
 * Derive line amounts when buying by carton/package.
 * @param {{ productsPerPackage: number|string, numberOfPackages: number|string, packagePrice: number|string }} input
 * @returns {null | { quantity: number, purchaseUnitPrice: number, total: number, packagePrice: number, productsPerPackage: number, numberOfPackages: number }}
 */
export function calcCartonLineAmounts({
  productsPerPackage,
  numberOfPackages,
  packagePrice
}) {
  const perPack = Number(productsPerPackage);
  const packs = Number(numberOfPackages);
  const pkgPrice = Number(packagePrice);

  if (!(perPack > 0) || !(packs > 0) || !Number.isFinite(pkgPrice) || pkgPrice < 0) {
    return null;
  }

  const quantity = Math.round(perPack * packs);
  const purchaseUnitPrice = roundMoney2(pkgPrice / perPack);
  const packagePriceRounded = roundMoney2(pkgPrice);
  // Keep total from carton price (not qty × unit) to avoid 0.01 drift
  const total = roundMoney2(packs * packagePriceRounded);

  return {
    quantity,
    purchaseUnitPrice,
    total,
    packagePrice: packagePriceRounded,
    productsPerPackage: perPack,
    numberOfPackages: packs
  };
}

/**
 * Live carton form preview (may be partial while typing).
 * @returns {{ totalUnits: number|null, unitCost: number|null, lineTotal: number|null }}
 */
export function calcCartonPreview({
  productsPerPackage,
  numberOfPackages,
  packagePrice
}) {
  const perPack = Number(productsPerPackage);
  const packs = Number(numberOfPackages);
  const pkgPrice = Number(packagePrice);

  const totalUnits = perPack > 0 && packs > 0 ? Math.round(perPack * packs) : null;
  const unitCost =
    perPack > 0 && Number.isFinite(pkgPrice) && pkgPrice > 0
      ? roundMoney2(pkgPrice / perPack)
      : null;
  const lineTotal =
    packs > 0 && Number.isFinite(pkgPrice) && pkgPrice > 0
      ? roundMoney2(packs * pkgPrice)
      : null;

  return { totalUnits, unitCost, lineTotal };
}

/** Rebuild package price when re-editing a line stored with unit cost. */
export function resolvePackagePriceFromUnit(unitPrice, productsPerPackage) {
  const unit = Number(unitPrice);
  const perPack = Number(productsPerPackage);
  if (!(unit > 0) || !(perPack > 0)) return '';
  return roundMoney2(unit * perPack);
}

/** Unit price from package (carton) price ÷ units per package. */
export function resolveUnitPriceFromPackage(packagePrice, productsPerPackage) {
  const pkg = Number(packagePrice);
  const perPack = Number(productsPerPackage);
  if (!(perPack > 0) || !Number.isFinite(pkg) || pkg < 0) return '';
  return roundMoney2(pkg / perPack);
}

/**
 * Live carton economics after converting package buy prices to unit.
 * Selling is always unit-based (sellingUnitPrice). Optional sellingPackagePrice
 * only fills unit sell when the unit field is empty (legacy draft / math helpers).
 * Margin % is identical whether compared on carton or unit: (V−A)/V.
 */
export function calcCartonEconomics({
  productsPerPackage,
  numberOfPackages,
  packagePrice,
  sellingPackagePrice,
  sellingUnitPrice
}) {
  const preview = calcCartonPreview({
    productsPerPackage,
    numberOfPackages,
    packagePrice
  });
  const perPack = Number(productsPerPackage);
  const packs = Number(numberOfPackages);
  let unitSell = Number(sellingUnitPrice);

  // Prefer unit sell (catalog / entry field). Fall back to package sell ÷ units if unit empty.
  if (!(Number.isFinite(unitSell) && unitSell > 0) && perPack > 0) {
    const pkgSell = Number(sellingPackagePrice);
    if (Number.isFinite(pkgSell) && pkgSell > 0) {
      unitSell = roundMoney2(pkgSell / perPack);
    }
  }

  const unitBuy = preview.unitCost;
  const hasUnitSell = Number.isFinite(unitSell) && unitSell > 0;
  const unitProfit =
    unitBuy != null && hasUnitSell ? roundMoney2(unitSell - unitBuy) : null;
  const marginPercent =
    unitBuy != null && hasUnitSell
      ? ((unitSell - unitBuy) / unitSell) * 100
      : null;
  const profitPerCarton =
    unitProfit != null && perPack > 0 ? roundMoney2(unitProfit * perPack) : null;
  // Prefer unit × total units so it matches table calcPurchaseLineEconomics exactly
  const lineProfit =
    unitBuy != null && hasUnitSell && preview.totalUnits != null
      ? roundMoney2((unitSell - unitBuy) * preview.totalUnits)
      : null;

  return {
    ...preview,
    unitSell: hasUnitSell ? unitSell : null,
    unitProfit,
    marginPercent,
    profitPerCarton,
    lineProfit,
    packs: packs > 0 ? packs : null,
    perPack: perPack > 0 ? perPack : null
  };
}
